import React, { Suspense, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Typography,
  Divider,
  Snackbar,
  Alert
} from "@mui/material";
import RichTextEditor from "./NotesRichTextEditor";
import { selectCurrentSuperAdmin } from "../../features/auth/authSelectors";
import { getCurrentUser, updateAdminData } from "../../features/auth/authSlice";
import { deleteFile } from "../../utils/deleteFile";
import { dataURLtoFile } from "../../utils/dataURLtoFile";
import { uploadFile } from "../../utils/uploadFile";

const Notes = () => {
  const admin = useSelector(selectCurrentSuperAdmin);
  const [noteType, setNoteType] = useState("Gym");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videoLink, setVideoLink] = useState(
    admin?.adminVideoLinks?.[noteType] || ""
  );
  const [isSavingVideo, setIsSavingVideo] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [value, setValue] = useState(admin?.adminNotes?.["Gym"] || "");
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  const handleNoteTypeChange = (event) => {
    const newNoteType = event.target.value;
    setNoteType(newNoteType);
    setValue(admin?.adminNotes?.[newNoteType] || "");
    setVideoLink(admin?.adminVideoLinks?.[newNoteType] || "");

    // Reset the editor selection
    if (editorRef.current) {
      editorRef.current.resetSelection();
    }
  };

  const handleSubmit = async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const savedNotes = admin?.adminNotes?.[noteType] || "";

      // Only process images if the text editor content was actually modified
      // If value is empty, use the previous savedNotes to avoid deleting existing images
      const contentToProcess = value || savedNotes;

      const parser = new DOMParser();
      const doc = parser.parseFromString(contentToProcess, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));
      console.log("Images found:", images);
      const prevUrls = new Set();

      // Collect previous image filenames to avoid re-uploading
      if (savedNotes) {
        const prevDoc = parser.parseFromString(savedNotes, "text/html");
        const prevImages = Array.from(prevDoc.querySelectorAll("img"));
        for (let prevImg of prevImages) {
          const prevSrc = prevImg.getAttribute("src");
          if (prevSrc) {
            prevUrls.add(prevSrc);
          }
        }
      }

      // Process new uploads if content was modified
      if (value) {
        for (let img of images) {
          let src = img.getAttribute("src");
          const originalSrc = src.split("?")[0];
          const fileName = originalSrc ? originalSrc?.split("/")?.at(-1) : "";

          if (src.includes(fileName)) {
            if (prevUrls.has(fileName)) {
              img.setAttribute("src", fileName);
              continue;
            }
          }

          if (src.startsWith("data:image")) {
            const file = dataURLtoFile(src, `image-${Date.now()}.png`);
            console.log("Uploading file:", file);
            const uniqueFileName = `${Date.now()}-${file.name}`;
            try {
              await uploadFile(
                file,
                uniqueFileName,
                process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
              );

              img.setAttribute("src", uniqueFileName);
            } catch (error) {
              console.error("Error uploading image:", error);
            }
          }
        }
      }

      console.log("Final HTML:", doc.body.innerHTML);
      dispatch(
        updateAdminData({ userType: noteType, adminNotes: doc.body.innerHTML })
      );
      setSnackbarOpen(true);
      setSnackbarMessage("Notes saved successfully");
      setTimeout(() => dispatch(getCurrentUser()), 1500);
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveVideoLink = async () => {
    try {
      if (isSavingVideo) return;
      setIsSavingVideo(true);

      dispatch(
        updateAdminData({
          userType: noteType,
          adminVideoLink: videoLink
        })
      );
      setTimeout(() => dispatch(getCurrentUser()), 1500);
    } catch (err) {
      console.log(err);
    } finally {
      setIsSavingVideo(false);
    }
  };

  return (
    <Box>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <RadioGroup
          row
          value={noteType}
          onChange={handleNoteTypeChange}
          sx={{
            gap: 2,
            "& .MuiFormControlLabel-root": {
              margin: 0
            }
          }}
        >
          {[
            { name: "Gyms", value: "Gym" },
            { name: "Coaches", value: "Coach" },
            { name: "Companies", value: "Company" },
            { name: "Affiliates", value: "Affiliate" }
          ].map(({ name, value }) => (
            <FormControlLabel
              key={value}
              value={value}
              control={
                <Radio
                  sx={{
                    display: "none"
                  }}
                />
              }
              label={
                <Box
                  sx={{
                    width: "120px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #1976d2",
                    backgroundColor:
                      noteType === value ? "#1976d2" : "transparent",
                    color: noteType === value ? "#fff" : "#1976d2",
                    cursor: "pointer",
                    fontWeight: 500,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor:
                        noteType === value ? "#1976d2" : "#A7C8E9"
                    }
                  }}
                >
                  {name}
                </Box>
              }
            />
          ))}
        </RadioGroup>

        <Button
          sx={{
            fontSize: "14px",
            padding: "16px 24px",
            background: "#1976d2",
            border: "1px solid #1976d2",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            height: "40px"
          }}
          onClick={handleSubmit}
          variant="outlined"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </Button>
      </Box>

      {/* Intro Video Link Section */}
      <Box width="100%" display="flex" flexDirection="column" mb={3}>
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Intro Video Link
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            fullWidth
            placeholder="Enter video URL (YouTube, Vimeo, etc.)"
            value={videoLink}
            onChange={(e) => setVideoLink(e.target.value)}
            size="medium"
            sx={{ flexGrow: 1 }}
          />
          <Button
            sx={{
              fontSize: "14px",
              padding: "10px 20px",
              background: "#1976d2",
              border: "1px solid #1976d2",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              height: "40px",
              minWidth: "100px"
            }}
            onClick={handleSaveVideoLink}
            variant="outlined"
            disabled={isSavingVideo}
          >
            {isSavingVideo ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box width="100%">
        <Typography variant="subtitle1" fontWeight={600}>
          Notes Content
        </Typography>
        <Suspense fallback={<div>Loading...</div>}>
          <RichTextEditor
            ref={editorRef}
            isLargeEditorNeeded
            value={value}
            setValue={setValue}
          />
        </Suspense>
      </Box>
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notes;
